// CLI v0.8 — `cloudgrid env list [<name>]`. Spec v17 §13.8.

import { Command } from 'commander';
import { Command as CommanderCommand } from 'commander';
import { getV2Client, type IV2Client } from '../../api/v2-client.js';
import { handleV2Error } from '../../handle-error.js';
import { Output } from '../../output.js';
import { addOutputOption, isStructuredOutput } from '../../services/output-format.js';
import { resolveEntityTarget } from '../../services/resolve-entity-target.js';

export type EnvSource = 'platform' | 'user' | 'app-default';

interface EnvListResponse {
  env: Array<{ key: string; value: string; source?: EnvSource }>;
  expected_secrets?: string[];
}

export interface RunEnvListOpts {
  client: IV2Client;
  name: string | undefined;
  json?: boolean;
  output?: string;
  cwd?: string;
}

const SOURCE_LABEL: Record<EnvSource, string> = {
  platform: 'platform (read-only)',
  user: 'user',
  'app-default': 'app-default',
};

export async function runEnvList(opts: RunEnvListOpts): Promise<void> {
  const target = await resolveEntityTarget(opts.client, opts.name, opts.cwd);
  // include_source surfaces the full union (platform-config + app-defaults +
  // user-env) so the user can see values they could not previously list — which
  // matters because the reserved-key check refused overrides on values they
  // could not see (backlog 074-D).
  const resp = await opts.client.get<EnvListResponse>(
    `/api/v2/runtimes/${encodeURIComponent(target.entity_id)}/env`,
    { include_source: 'true' },
  );
  const out = Output.fromOpts({ json: !!opts.json, quiet: false, output: opts.output });
  if (out.isStructured) {
    // Structured output (json|yaml): emit the env array (shape per API
    // response). When the server includes source tags, callers see them on
    // each row.
    const payload: Record<string, unknown> = { env: resp.env };
    if (resp.expected_secrets && resp.expected_secrets.length > 0) {
      payload.expected_secrets = resp.expected_secrets;
    }
    out.json(() => payload);
    return;
  }
  if (resp.env.length === 0) {
    process.stdout.write(`No env vars set on ${target.name}.\n`);
    if (resp.expected_secrets && resp.expected_secrets.length > 0) {
      process.stdout.write(`Expected secrets: ${resp.expected_secrets.join(', ')}\n`);
    }
    return;
  }

  const hasSource = resp.env.some((row) => row.source !== undefined);
  if (!hasSource) {
    // Pre-074 server response — fall back to the original key=value rendering.
    for (const { key, value } of resp.env) {
      process.stdout.write(`${key}=${value}\n`);
    }
    return;
  }

  const keyWidth = Math.max(3, ...resp.env.map((r) => r.key.length));
  const valueWidth = Math.max(5, ...resp.env.map((r) => r.value.length));
  const header = `${'KEY'.padEnd(keyWidth)}  ${'VALUE'.padEnd(valueWidth)}  SOURCE`;
  process.stdout.write(header + '\n');
  for (const row of resp.env) {
    const sourceLabel = row.source ? SOURCE_LABEL[row.source] : '';
    process.stdout.write(`${row.key.padEnd(keyWidth)}  ${row.value.padEnd(valueWidth)}  ${sourceLabel}\n`);
  }
  if (resp.expected_secrets && resp.expected_secrets.length > 0) {
    process.stdout.write(`\nExpected secrets: ${resp.expected_secrets.join(', ')}\n`);
  }
}

export const envListCommand: CommanderCommand = addOutputOption(
  new Command('list')
    .description('List env vars for a runtime')
    .argument('[name]', 'Entity slug or id (defaults to the entity linked in this directory)')
    .option('--json', 'JSON output (alias of -o json)'),
).action(async (name: string | undefined, opts: { json?: boolean; output?: string }) => {
  try {
    const client = await getV2Client();
    await runEnvList({ client, name, json: opts.json, output: opts.output });
  } catch (err) {
    await handleV2Error(err, { json: isStructuredOutput(opts) });
  }
});
